import React, { useEffect, useState } from 'react'
import { Icon, Message } from 'semantic-ui-react'

import './EnvironmentBanner.scss'

const BANNER_HEIGHT = 41 // Height of the banner in pixels

const getEnvironmentInfo = () => {
  const hostname = window.location.hostname
  // Must be a bare, static `process.env.X` read — Vite `define` replaces it
  // with a literal at build time. Guards like `typeof process` or
  // `process.env?.X` defeat the replacement and the value comes back undefined
  // in the browser. Every key read this way must be listed in ENV_KEYS in
  // vite.config.mjs, or the deployed bundle throws ReferenceError.
  const backendEnv = process.env.BACKEND_ENV

  // Deployed to staging - detect from hostname
  if (hostname.includes('staging')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>STAGING</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Deployed to development - detect from hostname
  if (hostname.includes('dev')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>DEVELOPMENT</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Deployed to sales - detect from hostname
  if (hostname.includes('sales') || hostname.includes('demo')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>DEMO</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Local FE with remote backend - use BACKEND_ENV
  if (hostname === 'localhost' || hostname.includes('lvh.me')) {
    if (backendEnv && ['dev', 'staging', 'production'].includes(backendEnv)) {
      const isProduction = backendEnv === 'production'
      return {
        show: true,
        message: (
          <>
            Connected to <strong>{backendEnv.toUpperCase()}</strong> backend
          </>
        ),
        isProduction,
      }
    }
    // Local backend (default)
    return {
      show: true,
      message: (
        <>
          Connected to <strong>LOCAL</strong> backend
        </>
      ),
      isProduction: false,
    }
  }

  // Production or unknown - no banner
  return { show: false }
}

const EnvironmentBanner = () => {
  const [dismissed, setDismissed] = useState(false)
  const envInfo = getEnvironmentInfo()

  // Set CSS variable on body to offset navbar and content
  useEffect(() => {
    if (envInfo.show && !dismissed) {
      document.body.style.setProperty(
        '--environment-banner-height',
        `${BANNER_HEIGHT}px`,
      )
    } else {
      document.body.style.setProperty('--environment-banner-height', '0px')
    }

    return () => {
      document.body.style.setProperty('--environment-banner-height', '0px')
    }
  }, [envInfo.show, dismissed])

  if (!envInfo.show || dismissed) {
    return null
  }

  return (
    <Message
      className={`environment-banner ${
        envInfo.isProduction ? 'production-warning' : ''
      }`}
      info={!envInfo.isProduction}
      negative={envInfo.isProduction}
      icon
      onDismiss={() => setDismissed(true)}
    >
      <Icon name={envInfo.isProduction ? 'warning sign' : 'info circle'} />
      <Message.Content>{envInfo.message}</Message.Content>
    </Message>
  )
}

export default EnvironmentBanner
